//
// Pan and zoom ("hand") input controls. Adapted from MentalCanvas C++ code.
// (c) 2016-2020 Mental Canvas Inc.

import {
    Camera,
    PerspectiveCamera,
    Quaternion,
    Sphere,
    Vector2,
    Vector3
} from 'three';
import {
    _Math,
    CameraUtils,
    RBT
} from './utils';
import {
    DrawableTool,
    DrawableToolEventResponse,
    ManipDelta,
    ManipEvent,
    ManipEventType
} from './MCDrawableTool';
import {
    MCScene
} from './mcsrc';

// Our own internal state-tracking during a manipulation input event.
enum ManipState {
    MS_Inactive = 0,    // No manip in progress.
    MS_Undecided,       // Manip has started, not yet classified.
    MS_TranslateOnly,
    MS_Freeform
};

export class HandInterface extends DrawableTool
{
    constructor(InScene: MCScene, InCamera: PerspectiveCamera, InViewportDims: Vector2)
    {
        super(InViewportDims);
        this.m_Scene = InScene;
        this.m_Camera = InCamera;

        // WebViewer: settings specifically for here.
        this.m_bKeepSceneOnScreen = true;
        this.m_bEnableRotate = false;
        this.m_bZoomThroughEnabled = true;
    }
    dispose()
    {
        super.dispose();
        this.m_Scene = null;
    }

    ProcessEvent(InEvent: ManipEvent): DrawableToolEventResponse {
        //AssertMsg( m_Canvas, TEXT("Tool's canvas is null - tool should not yet be receiving events!") );
        //AssertMsg( m_SimpleScene, TEXT("Tool's scene is null - tool should not yet be receiving events!"));

        switch ( InEvent.GetType() )
        {
        case ManipEventType.ET_Started:
            this.m_ManipState = ManipState.MS_Undecided;
            this.m_AngleRad = 0;
            this.m_FilteredScale = 1;
            this.m_UnprocessedScale = 1;
            this.m_bBrokeScaleDeadzone = false;
            this.EnsureOriginInFrontOfCam(this.m_Camera);
            // WebViewer: bounding sphere is constant
            //this.InitBoundingSphere();
            this.OnManipStarting();
            break;

        case ManipEventType.ET_Completed:
            this.OnManipCompleted();
            this.m_ManipState = ManipState.MS_Inactive;
            this.m_AngleRad = 0; // TODO: necessary?
            this.m_FilteredScale = 1; // TODO: necessary?
            this.m_UnprocessedScale = 1; // TODO: necessary?
            this.m_bBrokeScaleDeadzone = false; // TODO: necessary?
            // WebViewer: bounding sphere is constant
            //this.m_BoundingSphere.center.set(0,0,0);
            //this.m_BoundingSphere.radius = 1; // TODO: necessary?
            //this.m_bBoundingSphereSet = false;
            break;
        default:
            break;
        }

        if ( InEvent.HasDelta() )
            this.ProcessManipDelta( InEvent );

        //m_SimpleScene->RequestRedraw();
        return DrawableToolEventResponse.ER_ChainEvent;
    }

    EnsureOriginInFrontOfCam(InCam: Camera)
    {
        const Depth : number = -( CameraUtils.GetRBT(InCam).inverse().multiplyVec3(this.m_Origin ).z);
        if (Depth < 1e-4)
            this.m_Origin = this.m_Scene.NavmodeComputedCenter().center;
    }

    CalcScreenBoundRad() : number
    {
        var right : Vector3 = CameraUtils.GetRight(this.m_Camera);
        right.normalize();
        const center : Vector2 = CameraUtils.GetView(this.m_Camera, this.m_ViewportDims, this.m_BoundingSphere.center);
        const edge : Vector2 = CameraUtils.GetView(this.m_Camera, this.m_ViewportDims, this.m_BoundingSphere.center.clone().add(right.clone().multiplyScalar(this.m_BoundingSphere.radius)));
        return edge.distanceTo(center);
    }

    RemoveDeadzoneAngle(rRad: number, DeadAngleRad: number): number
    {
        if ( rRad > 0 )
            return Math.max(0, rRad - DeadAngleRad);
        else
            return Math.min(0, rRad + DeadAngleRad);
    }
    
    OnManipStarting() {} // listening not implemented
    OnManipCompleted() {} // listening not implemented
    OnManipApplyDelta(InDelta: ManipDelta, InNewAngleDeltaRad: number, InScaleFactor: number) {} // listening not implemented

    ProcessManipDelta(InEvent: ManipEvent)
    {
        const InDelta : ManipDelta = InEvent.GetDelta();

        // Angle.
        var NewAngleDeltaRad : number = 0;
        if ( this.m_ManipState != ManipState.MS_TranslateOnly && this.IsRotateEnabled() )
        {
/*    #if MC_PLATFORM == MC_PLATFORM_IOS
            const Deg DeadzoneAngle( 0.f ); // No deadzone on iOS.
    #else*/
            const DeadzoneAngleRad : number = InEvent.IsSpoofed() ? 0 : 15 * _Math.DEG2RAD; // No deadzone for 'spoofed' events.
//    #endif
            const OldDeadAngleRad = this.RemoveDeadzoneAngle( this.m_AngleRad, DeadzoneAngleRad );
            this.m_AngleRad += InDelta.GetAngleRad();
            const NewDeadAngleRad = this.RemoveDeadzoneAngle( this.m_AngleRad, DeadzoneAngleRad );
            NewAngleDeltaRad = NewDeadAngleRad - OldDeadAngleRad;
        }

        // Scale.
        this.m_FilteredScale *= .85;
        this.m_FilteredScale += .15 * InDelta.GetScale();
        this.m_UnprocessedScale += this.m_FilteredScale - 1;

        var ScaleFactor : number = 1;
        if ( this.m_ManipState != ManipState.MS_TranslateOnly )
        {
            if ( InEvent.IsSpoofed() )
            {
                ScaleFactor = InDelta.GetScale();
            }
            else if ( !this.m_bBrokeScaleDeadzone )
            {
   // #if MC_PLATFORM != MC_PLATFORM_IOS // No scale deadzone on iOS.
                const ScaleAccumThreshold : number = .08; // Initial scale deadzone to overcome.
                if (Math.abs(this.m_UnprocessedScale) > ScaleAccumThreshold)
   // #endif
                {
                    this.m_bBrokeScaleDeadzone = true;
                    // Discard anything that's been accumulated so far.
                    // TODO: do we really want to discard the first frame worth of scaling? It adds lag...
                    this.m_UnprocessedScale = 0;
                    this.m_FilteredScale = 1;
                }
            }
            // After breaking the deadzone, subsequent scale needs to accumulate to this value before applying.
            else// if ( Abs( m_UnprocessedScale ) > .005f )
            {
                ScaleFactor = 1 + this.m_UnprocessedScale;
                this.m_UnprocessedScale = 0;
            }

            // WebViewer: Bounding sphere is constant
            // Should be done by ManipStarting already.
            // Assert(m_bBoundingSphereSet);
            //if (!m_bBoundingSphereSet)
            //    InitBoundingSphere();
            
            if (this.m_bKeepSceneOnScreen && this.m_BoundingSphere.radius > 0)
            {
                // Limit zooming out to ensure that the scene's pixel area is at least 50% of the screen's narrower dimension.
                // Radius of the bounding sphere in screen pixels
                const BoundRadPre : number = this.CalcScreenBoundRad();
                // Desired minimum radius
                const BoundRadMin : number = Math.min(this.m_ViewportDims.x, this.m_ViewportDims.y) * .5;
                ScaleFactor = Math.min(ScaleFactor, Math.max(Math.sqrt(BoundRadPre / BoundRadMin), 1));
                // We're assuming the following. It doesn't appear to be quite exactly true, but behavior is fine nonetheless.
                // Assert(Equal(AreaPost, AreaPre * Sqr(1.f / InScaleFactor), 1e-1f));
            }
        }

        this.OnManipApplyDelta( InDelta, NewAngleDeltaRad, ScaleFactor );
    }

    IsRotateEnabled() : boolean {
        return this.m_bEnableRotate;
    }
    SetOrigin(origin: Vector3)
    {
        this.m_Origin.copy(origin);
    }

    protected m_Camera : PerspectiveCamera;
    protected m_Origin : Vector3 = new Vector3;     // The worldspace 'center' position around which the hand operation happens.
    protected m_ManipState : ManipState = ManipState.MS_Inactive;
    // Bounding sphere: set at start of a manip, retained during manip.
	// Computed and used only bKeepSceneOnScreen has been set.
    m_BoundingSphere : Sphere;
    m_bBoundingSphereSet : boolean = false;

    // Cumulative rotation angle - helps with behaviour
	// at particular moments (e.g., dead zone around angle=0.f).
    protected m_AngleRad : number;

	// Cumulative scale - lets us filter in scale from manips.
	protected m_FilteredScale : number = 1;
	protected m_UnprocessedScale : number = 1;

	// True once the scale deadzone has been broken for a particular manip.
	protected m_bBrokeScaleDeadzone : boolean = false;

	/////////////////////////////////////////////////////////////////////////
	// Client-configurable settings for hand behaviour.
	protected m_bEnableRotate : boolean = true;			// If true, rotation (twist) is enabled.
	protected m_bKeepSceneOnScreen : boolean = false;		// True in Player, false in Authoring. 
    protected m_bZoomThroughEnabled : boolean = false;	// When false (e.g. DrawMode) the camera is not allowed zoom (z-translate) through the active canvas.

    protected m_Scene: MCScene;
};


export class HandCamera extends HandInterface
{
    constructor(scene: MCScene, camera: PerspectiveCamera, InViewportDims: Vector2)
    {
        super(scene, camera, InViewportDims);
    }

    // Return value: "stiffness" to apply during pan, to resist further panning when
    // past the limit.
/*    ApplyPanConstraints(bTestOnly: boolean, deltat: number): number {
        const rubberBandDuration: number = 0.1;
        const eps : number = 0.001;
        if (this.m_groundPlane) {
            const groundConDistMax : number = 0;
            // Positive groundConDistPre === below ground plane (since positive distanceTo === above)
            const groundConDistPre : number = -this.m_groundPlane.distanceToPoint(this.m_Camera.position);
            if (groundConDistPre - groundConDistMax > eps) {
                if (!bTestOnly) {
                    // TODO: ground plane normal, or projected onto camera plane? (i.e., a reverse pan)
                    const dir : Vector3 = this.m_groundPlane.normal;
                    deltat = Math.min(deltat, 0.05);
                    this.m_Camera.position.addScaledVector(dir,
                        (groundConDistPre - groundConDistMax) * Math.min(1, deltat / rubberBandDuration));
                    //console.log("groundConDistPre = " + groundConDistPre.toFixed(2) + " dt = " + deltat.toFixed(2));
                }
                return (groundConDistPre - groundConDistMax)/0.1;
            }
            if (this.m_ceilingHeight > 0 && groundConDistPre < 0) {
                const ceilingConDistMax : number = 0;
                // Positive ceilingConDistPre === above ceiling plane
                const ceilingConDistPre : number = this.m_groundPlane.distanceToPoint(this.m_Camera.position) - this.m_ceilingHeight;
                if (ceilingConDistPre - ceilingConDistMax > eps) {
                    if (!bTestOnly) {
                        // TODO: ground plane normal, or projected onto camera plane? (i.e., a reverse pan)
                        const dirOpp : Vector3 = this.m_groundPlane.normal;
                        deltat = Math.min(deltat, 0.05);
                        this.m_Camera.position.addScaledVector(dirOpp,
                            -(ceilingConDistPre - ceilingConDistMax) * Math.min(1, deltat / rubberBandDuration));
                        //console.log("ceilingConDistPre = " + ceilingConDistPre.toFixed(2) + " dt = " + deltat.toFixed(2));
                    }
                    return (ceilingConDistPre - ceilingConDistMax)/0.1;
                }
                }
            return 0;
        }

        if (this.m_PanMaxPercent > 10000)
            return 0;

        // TODO: we really want camera at bookmark position, to constraint frame origin. Really, just depth
        // from bookmark to constraint frame would suffice.

        const conDistMax : number = 0.5 * (Math.tan(this.m_Camera.fov * _Math.DEG2RAD / 2) +
            // Actually, average the vertical height and the horizontal width
            Math.tan(this.m_Camera.fov * _Math.DEG2RAD / 2) * this.m_Camera.aspect)
            * this.m_Scene.controls.constraintFrame.translation.distanceTo(this.m_Origin)
            // Now, take a percentage of that.
            * this.m_PanMaxPercent;
        const lookDir : Vector3 = this.m_Camera.getWorldDirection(new Vector3);
        const rCamPre : Ray = new Ray(this.m_Camera.position, lookDir);
        const camPreClosestPt : Vector3 = rCamPre.GetPos(rCamPre.ComputeTFromPos(this.m_Origin));
        // Distance - i.e., net total pan distance from the original constraint position.
        const conDistPre : number = camPreClosestPt.distanceTo(this.m_Origin);
        if (conDistPre - conDistMax > eps && !bTestOnly) {
            // Pan direction for camera that will bring us closer to rotcenter.
            var dir : Vector3 = this.m_Origin.clone().sub(camPreClosestPt);
            dir.normalize();
            // Pan amount: enough to hit the maximum pan amount after the rubberBandDuration time period elapses.
            const rubberBandDuration: number = 0.1;
            this.m_Camera.position.addScaledVector(dir, (conDistPre - conDistMax) * Math.min(1, deltat / rubberBandDuration));
        }
        return Math.max(0, conDistPre / conDistMax - 1);

    }*/

    OnManipCompleted()
    {
        super.OnManipCompleted();
        //switch(AppState) {
        // ...
        // case SimpleScene::AS_NavMode:
        // "Classic" player controls: don't update the center, except due to bookmark change.
        if (this.m_Scene.controls.enableAuthoringNavMode)
            this.m_Scene.SetNavModeCenter();
        //IncCameraMoveCounter();
        //break;
        //}
    }
    OnManipApplyDelta(InDelta: ManipDelta, InNewAngleDeltaRad: number, InScaleFactor: number)
    {	
        //////////////////////////////////////////////////////////////////////////
        // Compute rotation.
        const InScreenOrigin : Vector2 = InDelta.GetOrigin();
        const InScreenTranslationDelta : Vector2 = InDelta.GetTranslation();

        const PrevCamRBT = CameraUtils.GetRBT(this.m_Camera);
        // WebViewer - not required, constant during life of app
        //if (!m_bBoundingSphereSet)
        //  InitBoundingSphere();

        // Do visibility testing against a smaller sphere. This ensures that we stop movement once 80% of the bounding sphere
        // is outside the frustrum - and thus that some part of the outer 20% of the sphere is still visible.
        // (Given the looseness of the spheres, even this doesn't do much...)
        var TestSphere = new Sphere(this.m_BoundingSphere.center, this.m_BoundingSphere.radius * 0.8);
        const bPrevVisible: boolean = CameraUtils.IsVisibleSphere_CameraSlow(this.m_Camera, TestSphere);

        var depth : number = -PrevCamRBT.clone().inverse().multiplyVec3(this.m_Origin).z;
        // if (this.m_Scene.controls.enableAuthoringNavMode)
        //     depth = Math.max(depth, this.m_BoundingSphere.radius*0.1);
        depth = Math.min(Math.max(depth, this.m_BoundingSphere.radius*0.1), this.m_BoundingSphere.radius*0.3);

        const OriginPoint : Vector3 =
            CameraUtils.GetWorld2(this.m_Camera, this.m_ViewportDims, InScreenOrigin.x, InScreenOrigin.y, depth);

        const Rotation : RBT = new RBT( new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), InNewAngleDeltaRad), new Vector3(0,0,0));
        const Auxiliary : RBT = new RBT( PrevCamRBT.rotation, OriginPoint );
    
        // Do a rotation to the camera RBT around its local look axis.
        const NewCamRBT : RBT = Rotation.doToWRT( PrevCamRBT, Auxiliary );
    
        // ...
        // perform shift
        const TranslationPoint : Vector3 = CameraUtils.GetWorld2(this.m_Camera, this.m_ViewportDims,
                InScreenOrigin.x + InScreenTranslationDelta.x,
                InScreenOrigin.y + InScreenTranslationDelta.y,
                depth / InScaleFactor);

    	//////////////////////////////////////////////////////////////////////////
    	// Apply rotation.
        this.m_Camera.setRotationFromQuaternion(NewCamRBT.rotation);

        //////////////////////////////////////////////////////////////////////////
        // Apply translation.
        // Decompose into XY (pan) and Z (zoom) components. We apply stiffness here - after scaling - because
        // scaling with an off-center origin point will introduce XY panning.
        const lookDir = this.m_Camera.getWorldDirection( new Vector3 );
        const DeltaZoom = lookDir.clone().multiplyScalar(lookDir.dot(TranslationPoint.clone().sub(OriginPoint)));
        const DeltaPan = TranslationPoint.clone().sub(OriginPoint).sub(DeltaZoom);

        // TWEAKS - adjust a position-based framework (above) into an impulse-based framework.
        //
        // We treat the user input as current velocity, then test constraints and apply impulses
        // (delta-momentums) against it and iterate until all "hard" constraints are satisfied.
        
        const deltat:number = 1/30;
        const mass:number = 1;  // Pointless for a one-body simulation, but let's keep the math coherent.
        const userImpulse : Vector3 = DeltaPan.clone().add(DeltaZoom).multiplyScalar(mass * 1/deltat);
        const velocity = userImpulse.clone().multiplyScalar(1 / mass);
        this.m_Camera.position.addScaledVector(velocity, deltat);

        /*

        // Panning rubber band effect part #1.
        // Regular panning: double stiffness for every 10% beyond pan constraint.
        // Zooming: if we've broken the deadzone, make the pan stiffness much steeper (2^-(x^4) instead of 2^-x).
        //    We don't want much bounce-back.
        var panStiffness = this.ApplyPanConstraints(true, 0.0) / 0.1;
        if (this.m_bBrokeScaleDeadzone) panStiffness = (panStiffness ** 2) ** 2;
        panStiffness = Math.pow(2.0, panStiffness);

        // TODO: only ground-plane component...
        // TODO: port zoom stiffness to authoring.
        const zoomStiffness = Math.min(16, panStiffness);
        this.m_Camera.position.copy(
            PrevCamRBT.translation.clone().addScaledVector(DeltaPan, 1/panStiffness)
            .addScaledVector(DeltaZoom, 1/zoomStiffness));

        // If the camera is not permitted to penetrate the active canvas, back off the camera position.
        if ( !this.m_bZoomThroughEnabled )
        {
            const CameraRBT: RBT = CameraUtils.GetRBT( this.m_Camera );
            const Depth : number = -(CameraRBT.inverse().multiplyVec3(this.m_Origin).z);
            if ( Depth < this.m_Camera.near + 1e-4 )
                this.m_Camera.position.add(CameraRBT.getZAxis3().multiplyScalar(this.m_Camera.near + 1e-4 - Depth) );
            else if ( Depth > this.m_Camera.far - 1e-4 ) // TODO: is this epsilon kosher given large far-dists?
                this.m_Camera.position.sub(CameraRBT.getZAxis3().multiplyScalar(Depth - this.m_Camera.far + 1e-4) );
        }
    
        if (this.m_bKeepSceneOnScreen && this.m_BoundingSphere.radius > 0)
        {
            // If we were (sort of) onscreen and are now offscreen, then revert the complete manip.
            // (It's possible to do this via either translation, scaling or rotation.)
            //
            // In reality, the bounding sphere is not very "tight" to the geometry. So what happens is that
            // "bounding sphere onscreen" is equivalent to "canvases are just offscreen."
            if (bPrevVisible && !CameraUtils.IsVisibleSphere_CameraSlow(this.m_Camera, TestSphere)) {
                this.m_Camera.setRotationFromQuaternion(PrevCamRBT.rotation);
                this.m_Camera.position.copy(PrevCamRBT.translation);
                // TODO: will this *truly* restore the angle member? Does it matter - only
                // really relevant for deadzone calculations
                //this.m_AngleRad -= InNewAngleDeltaRad;
            }
        }*/

        this.m_Scene.cameraAnimator.SetOnBookmarkPath(false);
    
        // Route to super / listener.
        super.OnManipApplyDelta( InDelta, InNewAngleDeltaRad, InScaleFactor );
    }
    
    SetZoomThroughEnabled(bValue: boolean)
    {
        this.m_bZoomThroughEnabled = bValue;
    }

    // Tighter pan constraints; only allow a limited pan from the "constraint frame" (e.g., bookmark location).
    // Used in ApplyPanConstraints
    m_PanMaxPercent : number;
};

